import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setScrolled(false);
    
    // Force scroll to top on page changes, with multiple attempts to ensure it works
    if (!location.hash) {
      window.scrollTo(0, 0);
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }, 0);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 200);
    }
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, location.hash]);

  const navItems = [
    { path: '/', label: t('navHome') },
    { path: '/gallery', label: t('navGallery') },
    { path: '/location', label: t('navLocation') },
  ];

  return (
          <nav className="fixed w-full z-50 transition-all duration-500 bg-site-bg shadow-xl py-2 border-b border-carnival-charcoal/20">
      <div className="section-padding">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <motion.div 
              key={location.pathname}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
                              <img 
                src="/assets/carnival_logo.PNG" 
                alt="Carnival Restaurant" 
                className="h-12 w-auto object-contain transition-opacity duration-300"
              />
              <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full" />
            </motion.div>
          </Link>

          <div className="hidden lg:flex items-center gap-10">
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                                      className={`relative font-body text-lg tracking-wide transition-colors duration-300 text-gray-800 hover:text-carnival-burgundy ${location.pathname === item.path ? 'text-carnival-burgundy' : ''}`}
                >
                  <span className="relative">
                    {item.label}
                    <span className={`absolute -bottom-2 left-0 w-full h-0.5 bg-black transform transition-transform duration-300 ${
                      location.pathname === item.path ? 'scale-x-100' : 'scale-x-0'
                    } group-hover:scale-x-100`} />
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="ml-8"
            >
              <LanguageSelector scrolled={true} />
            </motion.div>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-2xl transition-colors duration-300 text-gray-800"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-site-bg shadow-2xl border-t border-carnival-charcoal/20"
          >
            <div className="section-padding py-6">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-4 text-lg font-body tracking-wide text-carnival-charcoal hover:text-carnival-burgundy transition-colors ${
                      location.pathname === item.path ? 'text-carnival-burgundy' : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="pt-4 border-t border-carnival-gold/20"
              >
                <LanguageSelector scrolled={true} />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;